// Auth context — owns the admin session lifecycle.
//
// Token model (LOCKED rules): access_token in JS memory only (lib/api tokenStore);
// refresh_token in an httpOnly cookie the backend sets for c-ls-admin (per-client
// name refresh_token__c-ls-admin, restful 00 §A.7). On boot we
// attempt ONE silent cookie-refresh so a page reload survives without re-login.
//
// Role: derived from the JWT `role` claim (decodeJwtClaims) — display/gating ONLY,
// never a security boundary (server requireAdmin is the real gate). A silent refresh
// yields a token but NO user object, so `user` may be null while authenticated;
// `role` is still available because it is read from the token claims, not the user
// object. Allowed console roles: full admin roles plus cs-readonly. Server-side
// live DB re-check remains the real security boundary.

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import {
  decodeJwtClaims,
  hasSessionHint,
  login as apiLogin,
  logout as apiLogout,
  onUnauthorized,
  refreshAccessToken,
  tokenStore,
} from '../lib/api';
import type { AdminUserClaims } from '../lib/api';

export type AuthStatus = 'booting' | 'authenticated' | 'anonymous';

/** Roles permitted into the console UI. Server still enforces per-endpoint. */
export const ADMIN_ROLES = ['admin', 'security-officer', 'platform-owner', 'cs-readonly'] as const;
export type AdminRole = (typeof ADMIN_ROLES)[number];

export function isAdminRole(role: string | null | undefined): role is AdminRole {
  return role === 'admin' || role === 'security-officer' || role === 'platform-owner' || role === 'cs-readonly';
}

export function canAdminWrite(role: string | null | undefined): boolean {
  return role === 'admin' || role === 'security-officer' || role === 'platform-owner';
}

export function canGrantRoles(role: string | null | undefined): boolean {
  return role === 'platform-owner';
}

export interface AuthContextValue {
  status: AuthStatus;
  /** May be null right after a silent refresh (see header comment). */
  user: AdminUserClaims | null;
  /** Role from the JWT claim — survives a silent refresh even when `user` is null. */
  role: string | null;
  /** user_id from the JWT `sub` claim — survives silent refresh (unlike `user`). */
  userId: string | null;
  /** Scope array from the JWT claim (e.g. includes 'admin-full'). */
  scopes: string[];
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/** Pull identity + role + scope off the currently-held access token. */
function readClaims(): { userId: string | null; role: string | null; scopes: string[] } {
  const claims = decodeJwtClaims(tokenStore.get());
  return {
    userId: typeof claims?.sub === 'string' ? claims.sub : typeof claims?.user_id === 'string' ? (claims.user_id as string) : null,
    role: typeof claims?.role === 'string' ? claims.role : null,
    scopes: Array.isArray(claims?.scope) ? (claims!.scope as string[]) : [],
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('booting');
  const [user, setUser] = useState<AdminUserClaims | null>(null);
  const [claims, setClaims] = useState<{ userId: string | null; role: string | null; scopes: string[] }>({ userId: null, role: null, scopes: [] });
  const booted = useRef(false);

  const endSession = useCallback(() => {
    tokenStore.clear();
    setUser(null);
    setClaims({ userId: null, role: null, scopes: [] });
    setStatus('anonymous');
  }, []);

  // Boot: one silent refresh off the httpOnly cookie — but only when the
  // session-hint cookie says a session may exist (an anonymous boot would
  // otherwise fire a guaranteed-400 refresh and pollute the console).
  useEffect(() => {
    if (booted.current) return;
    booted.current = true;
    if (!hasSessionHint()) {
      setStatus('anonymous');
      return;
    }
    refreshAccessToken().then(
      () => {
        setClaims(readClaims());
        setStatus('authenticated');
      },
      () => setStatus('anonymous')
    );
  }, []);

  // Session-expired hook: REST double-401 (refresh failed / second 401) → log out.
  useEffect(() => {
    onUnauthorized(endSession);
    return () => onUnauthorized(null);
  }, [endSession]);

  const login = useCallback(async (email: string, password: string) => {
    const data = await apiLogin(email, password);
    setUser(data.user ?? null);
    setClaims(readClaims());
    setStatus('authenticated');
  }, []);

  const logout = useCallback(async () => {
    await apiLogout();
    endSession();
  }, [endSession]);

  const value = useMemo<AuthContextValue>(
    () => ({ status, user, role: claims.role, userId: claims.userId, scopes: claims.scopes, login, logout }),
    [status, user, claims, login, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
